import { AxiosResponse } from "axios";

import apiClient from "../../../../shared/infrastructure/api/client";
import type { MeetingBackground } from "../../domain/meetingBackground";

export type { MeetingBackground } from "../../domain/meetingBackground";

export type MeetingBackgroundUpload = {
    upload_url: string;
    /** The object the slot was minted for. Sent back at registration unchanged. */
    storage_path: string;
};

export const MeetingBackgroundsAPI = {
    list: (): Promise<AxiosResponse<MeetingBackground[]>> =>
        apiClient.get("/meeting-backgrounds"),

    /** The shelf of the caller's centre. Empty for a therapist in none. */
    listCenter: (): Promise<AxiosResponse<MeetingBackground[]>> =>
        apiClient.get("/meeting-backgrounds/center"),

    uploadUrl: (
        contentType: string
    ): Promise<AxiosResponse<MeetingBackgroundUpload>> =>
        apiClient.post("/meeting-backgrounds/upload-url", {
            content_type: contentType,
        }),

    create: (background: {
        name: string;
        storage_path: string;
        content_type: string;
        /** Onto the centre's shelf. Administrators only. */
        for_center?: boolean;
    }): Promise<AxiosResponse<MeetingBackground>> =>
        apiClient.post("/meeting-backgrounds", background),

    remove: (id: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/meeting-backgrounds/${id}`),

    /** The picture's bytes, through our own API rather than from the bucket. */
    image: (id: string): Promise<AxiosResponse<Blob>> =>
        apiClient.get(`/meeting-backgrounds/${id}/image`, { responseType: "blob" }),
};

/**
 * The bytes, straight from the browser to the bucket.
 *
 * The same two-step upload a recording and a meditation track use: the file
 * never passes through our API, and the header has to be exactly the one the
 * URL was signed for or the signature does not cover the request.
 */
export async function uploadBackgroundImage(
    uploadUrl: string,
    file: Blob,
    contentType: string
): Promise<void> {
    const response = await fetch(uploadUrl, {
        headers: { "Content-Type": contentType },
        method: "PUT",
        body: file,
    });
    if (!response.ok) {
        throw new Error(`Background upload failed with status ${response.status}`);
    }
}
